import { Fragment, type ReactNode, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

/**
 * Standard paginated list/table template. Wraps rows in a card with dividers,
 * an optional column header, an empty state, and built-in pagination
 * (default 5 rows, adjustable to 10 / 25). Use this for EVERY table/list so
 * the app stays lightweight (lazy paging) and consistent.
 *
 * <DataList items={rows} renderRow={(r) => <Row .../>} header={<HeaderRow/>} />
 */
export function DataList<T>({
  items,
  renderRow,
  header,
  getKey,
  pageSize = 5,
  pageSizeOptions = [5, 10, 25],
  empty = 'Nothing here yet',
  className,
}: {
  items: T[]
  renderRow: (item: T) => ReactNode
  header?: ReactNode
  getKey?: (item: T, index: number) => string | number
  pageSize?: number
  pageSizeOptions?: number[]
  empty?: ReactNode
  className?: string
}) {
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(pageSize)

  const total = items.length
  const totalPages = Math.max(1, Math.ceil(total / size))
  const current = Math.min(page, totalPages)
  const start = (current - 1) * size
  const pageItems = items.slice(start, start + size)

  return (
    <Card className={className}>
      <CardContent className="p-0">
        {total === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">{empty}</div>
        ) : (
          <>
            {header && (
              <div className="border-b border-border px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {header}
              </div>
            )}
            <div className="divide-y divide-border">
              {pageItems.map((item, i) => (
                <Fragment key={getKey ? getKey(item, start + i) : start + i}>{renderRow(item)}</Fragment>
              ))}
            </div>

            {/* Pagination footer */}
            <div className="flex flex-col gap-3 border-t border-border px-4 py-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
              <span>
                Showing {start + 1}–{start + pageItems.length} of {total}
              </span>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  Rows
                  <select
                    value={size}
                    onChange={(e) => {
                      setSize(Number(e.target.value))
                      setPage(1)
                    }}
                    className="rounded-md border border-border bg-card px-2 py-1 text-foreground"
                  >
                    {pageSizeOptions.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setPage(current - 1)}
                    disabled={current <= 1}
                    aria-label="Previous page"
                    className={cn(
                      'rounded-md border border-border p-1 transition-colors',
                      current <= 1 ? 'opacity-40' : 'hover:bg-muted/60 text-foreground'
                    )}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="px-1 tabular-nums">
                    {current} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage(current + 1)}
                    disabled={current >= totalPages}
                    aria-label="Next page"
                    className={cn(
                      'rounded-md border border-border p-1 transition-colors',
                      current >= totalPages ? 'opacity-40' : 'hover:bg-muted/60 text-foreground'
                    )}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
